import { initializeApp } from "firebase/app";
import { posthog } from "posthog-js";
import { ConsentStatus, FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { isWeb, isAndroid } from "@core/plugins/mobile";
import {
  FIREBASE_API_KEY,
  FIREBASE_APP_ID,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_MEASUREMENT_ID,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
} from "@core/constants";
import { Logger } from "@core/helpers/logger.helpers";
import { Capacitor } from "@capacitor/core";

/**
 * Manages Firebase Analytics integration for mobile platforms
 * Handles initialization, consent management, and analytics events
 */
export class FirebaseManager {
  /**
   * List of consent types for Firebase Analytics
   * Used for both Android and iOS platforms
   */
  static consentTypes = ["ANALYTICS_STORAGE", "AD_STORAGE", "AD_USER_DATA", "AD_PERSONALIZATION"];

  /**
   * Determines consent status based on input value
   * @param {number|string} value - Consent value from PostHog or direct input
   * @returns {ConsentStatus} Firebase consent status (Granted/Denied)
   */
  static getConsentStatus(value) {
    const isGranted = typeof value === "number" ? value === 1 : value?.toLowerCase() === "granted";
    return isGranted ? ConsentStatus.Granted : ConsentStatus.Denied;
  }

  /**
   * Sets consent status for all consent types
   * @param {ConsentStatus} status - Consent status to apply
   */
  static async setConsents(status) {
    const consents = this.consentTypes.map((type) => ({ type, status }));
    await Promise.all(consents.map((consent) => FirebaseAnalytics.setConsent(consent)));
  }

  /**
   * Initializes Firebase for mobile platforms
   * Sets up analytics, consent management, and debug mode if needed
   */
  static async initialize() {
    if (isWeb) return;

    try {
      const config = {
        apiKey: FIREBASE_API_KEY,
        authDomain: FIREBASE_AUTH_DOMAIN,
        projectId: FIREBASE_PROJECT_ID,
        storageBucket: FIREBASE_STORAGE_BUCKET,
        messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
        appId: FIREBASE_APP_ID,
        measurementId: FIREBASE_MEASUREMENT_ID,
      };

      initializeApp(config);

      if (!Capacitor.isPluginAvailable("FirebaseAnalytics")) {
        Logger.warn("FirebaseAnalytics plugin unavailable");
        return;
      }

      await FirebaseAnalytics.setEnabled({ enabled: true });

      // Android-specific consent management
      if (isAndroid) {
        await this.setConsents(this.getConsentStatus(posthog.consent.storedConsent));
      }

      // Enable debug mode in development
      if (process.env.NODE_ENV === "development") {
        await FirebaseAnalytics.setEnabled({ enabled: true, debugModeEnabled: true });
      }
    } catch (error) {
      Logger.error("Firebase initialization failed:", error?.message || error);
    }
  }

  /**
   * Updates consent status across all consent types
   * @param {string|number} value - New consent value
   */
  static async updateConsent(value) {
    if (isWeb) return;

    try {
      await this.setConsents(this.getConsentStatus(value));
    } catch (error) {
      Logger.error("Consent update failed:", error?.message || error);
    }
  }

  /**
   * Logs an analytics event to Firebase
   * Updates consent status before logging
   * @param {Object} params - Event parameters
   * @param {string} params.name - Event name
   * @param {Object} params.params - Event data
   */
  static async logEvent({ name, params }) {
    if (isWeb) return;

    try {
      await this.updateConsent(posthog.consent.storedConsent);
      return await FirebaseAnalytics.logEvent({ name, params });
    } catch (error) {
      Logger.error("Event logging failed:", error);
    }
  }

  /**
   * Sets user properties in Firebase Analytics
   * @param {Object} properties - User properties to set
   */
  static async setUserProperties({ properties }) {
    try {
      return await FirebaseAnalytics.setUserProperty({ properties });
    } catch (error) {
      Logger.error("User properties update failed:", error);
    }
  }

  /**
   * Sets user ID in Firebase Analytics
   * @param {string} userId - User identifier
   */
  static async setUserId({ userId }) {
    try {
      return await FirebaseAnalytics.setUserId({ userId });
    } catch (error) {
      Logger.error("User ID update failed:", error);
    }
  }

  /**
   * Sets current screen name in Firebase Analytics
   * @param {string} screenName - Screen identifier
   */
  static async setCurrentScreen({ screenName }) {
    try {
      return await FirebaseAnalytics.setCurrentScreen({ screenName });
    } catch (error) {
      Logger.error("Screen update failed:", error);
    }
  }
}
